/* eslint-disable max-lines */
import {
  BottomSheetWithIntent,
  CbhIcon,
  Divider,
  Text,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined, isNil } from "@clipboard-health/util-ts";
import { Box, Collapse, DialogContent, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { WheelTimePicker } from "@src/appV2/redesign/components/WheelTimePicker/WheelTimePicker";
import { addHours, addMinutes, isAfter, isBefore } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "../../../components/Button";
import { DialogFooter } from "../../../components/DialogFooter";
import { type Duration } from "../../../DateTime/DurationInput";
import { DurationInput } from "../../../DateTime/DurationInput";
import { formatTimeWithTimezone } from "../../../utils/formatTimeWithTimezone";
import {
  type BreakErrorType,
  validateBreakTimestamps,
} from "../../MissedPunches/utils/validateBreakTimestamps";
import { isBoundaryError } from "../analytics/isBoundaryError";
import { inferDateFromTime } from "./utils/inferDateFromTime";
import { getLocalTimeForPicker, getUtcTimeFromPicker } from "./utils/timezoneConversion";

interface EditBreakTimeFormValues {
  breakStart?: Date;
  breakDuration: Duration;
}

interface EditBreakTimeBottomSheetProps {
  modalState: UseModalState;
  breakStartTime?: Date;
  defaultBreakDuration: Duration;
  breakDuration?: Duration;
  initialBreakSkipped?: boolean;
  workplaceTimezone: string;
  shiftStartAt: Date;
  shiftEndAt: Date;
  clockInTime: Date;
  clockOutTime: Date;
  verifiedBreakStartTime?: Date;
  verifiedBreakEndTime?: Date;
  isMissedPunchRequestEnabled: boolean;
  isShiftVerified: boolean;
  shiftId?: string;
  workplaceId?: string;
  onConfirm: (breakStartTime: Date | undefined, breakDuration: Duration) => void;
  onCancel?: () => void;
}

type ActiveSelector = "breakStart" | "breakDuration" | undefined;

// Helper function to format duration, showing "Skipped" when break is skipped
function formatBreakDuration(duration: Duration, isSkipped: boolean): string {
  if (isSkipped) {
    return "Skipped";
  }

  if (duration.hours > 0) {
    const minutesFormatted = duration.minutes.toString().padStart(2, "0");
    return `${duration.hours} hr ${minutesFormatted} min`;
  }

  return `${duration.minutes} min`;
}

// Helper function to calculate middle of shift
function calculateMiddleOfShift(clockInTime: Date, clockOutTime: Date): Date {
  const shiftDurationMs = clockOutTime.getTime() - clockInTime.getTime();
  return new Date(clockInTime.getTime() + shiftDurationMs / 2);
}

function formatBreakStartDisplay(
  breakSkipped: boolean,
  currentLocalBreakStartTime?: Date,
  workplaceTimezone?: string
): string {
  if (breakSkipped) {
    return "--:-- --"; // Show N/A for skipped breaks
  }

  if (isDefined(currentLocalBreakStartTime) && isDefined(workplaceTimezone)) {
    // Convert local time back to UTC, then format in workplace timezone
    const utcTime = getUtcTimeFromPicker(currentLocalBreakStartTime, workplaceTimezone);
    return formatTimeWithTimezone(utcTime, workplaceTimezone, false);
  }

  return "--:-- --";
}

export function EditBreakTimeBottomSheet(props: EditBreakTimeBottomSheetProps) {
  const {
    modalState,
    breakStartTime,
    defaultBreakDuration,
    breakDuration = defaultBreakDuration,
    initialBreakSkipped = false, // Default to false
    workplaceTimezone,
    clockInTime,
    clockOutTime,
    verifiedBreakStartTime,
    verifiedBreakEndTime,
    isMissedPunchRequestEnabled,
    shiftStartAt,
    shiftEndAt,
    isShiftVerified,
    shiftId,
    workplaceId,
    onConfirm,
    onCancel,
  } = props;

  const [activeSelector, setActiveSelector] = useState<ActiveSelector>();
  const [localBreakSkipped, setLocalBreakSkipped] = useState(initialBreakSkipped);
  const [saveAttempted, setSaveAttempted] = useState(false); // Track if save was attempted
  const [isPickerScrolling, setIsPickerScrolling] = useState(false);

  // Calculate default break start time for skipped breaks
  const getDefaultBreakStartTime = useCallback(() => {
    if (breakStartTime) {
      // Use existing break start time
      return getLocalTimeForPicker(breakStartTime, workplaceTimezone);
    }

    if (initialBreakSkipped) {
      // For skipped breaks, default picker to middle of shift
      const middleTime = calculateMiddleOfShift(clockInTime, clockOutTime);
      return getLocalTimeForPicker(middleTime, workplaceTimezone);
    }

    return undefined;
  }, [breakStartTime, initialBreakSkipped, clockInTime, clockOutTime, workplaceTimezone]);

  const { setValue, watch, reset } = useForm<EditBreakTimeFormValues>({
    defaultValues: {
      breakStart: getDefaultBreakStartTime(),
      breakDuration,
    },
    mode: "onSubmit",
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!modalState.modalIsOpen) {
      reset({
        breakStart: getDefaultBreakStartTime(),
        breakDuration,
      });
      setLocalBreakSkipped(initialBreakSkipped);
      setSaveAttempted(false);
      setActiveSelector(undefined);
    }
  }, [modalState.modalIsOpen, reset, getDefaultBreakStartTime, breakDuration, initialBreakSkipped]);

  const currentLocalBreakStartTime = watch("breakStart");
  const currentBreakDuration = watch("breakDuration");

  // Convert back to UTC for validation and submission
  const currentBreakStartTime = currentLocalBreakStartTime
    ? getUtcTimeFromPicker(currentLocalBreakStartTime, workplaceTimezone)
    : undefined;

  const calculateBreakEndTime = (startTime?: Date, duration?: Duration): Date | undefined => {
    if (!isDefined(startTime) || !isDefined(duration) || localBreakSkipped) {
      return undefined;
    }

    let endTime = new Date(startTime);
    if (duration.hours > 0) {
      endTime = addHours(endTime, duration.hours);
    }

    if (duration.minutes > 0) {
      endTime = addMinutes(endTime, duration.minutes);
    }

    return endTime;
  };

  const getValidationResult = (
    startTime?: Date,
    duration?: Duration
  ): { errorMessage?: string; errorType?: BreakErrorType } => {
    const endTime = calculateBreakEndTime(startTime, duration);

    // Use centralized validation when feature flag is enabled
    if (isMissedPunchRequestEnabled) {
      const validationResult = validateBreakTimestamps({
        breakStartTime: startTime,
        breakEndTime: endTime,
        clockInTime,
        clockOutTime,
        isShiftVerified,
        isBreakSkipped: localBreakSkipped,
        verifiedBreakStartTime,
        verifiedBreakEndTime,
      });

      // Return error message if validation failed
      if (!validationResult.isValid) {
        return {
          errorMessage: validationResult.message,
          errorType: validationResult.errorType,
        };
      }

      return {};
    }

    // No validation needed for skipped breaks
    if (localBreakSkipped || isNil(startTime)) {
      return {};
    }

    // Legacy validation (fallback when MPR is not enabled)
    if (isBefore(startTime, clockInTime)) {
      return { errorMessage: "You cannot start your break before your clock in time." };
    }

    if (isAfter(startTime, clockOutTime)) {
      return { errorMessage: "You cannot start your break after your clock out time." };
    }

    if (isDefined(endTime) && isAfter(endTime, clockOutTime)) {
      return { errorMessage: "You cannot end your break after your clock out time." };
    }

    return {};
  };

  // Only show error if save was attempted
  const { errorMessage } = saveAttempted
    ? getValidationResult(currentBreakStartTime, currentBreakDuration)
    : {};

  const handleConfirm = () => {
    setSaveAttempted(true); // Mark that save was attempted

    // For skipped breaks, pass undefined start time and 0 duration
    if (localBreakSkipped) {
      onConfirm(undefined, { hours: 0, minutes: 0 });
      modalState.closeModal();
      return;
    }

    // Check for errors on save
    const currentValidation = getValidationResult(currentBreakStartTime, currentBreakDuration);

    // Track boundary violations when user attempts to save with an error
    if (isBoundaryError(currentValidation.errorType)) {
      logEvent(APP_V2_USER_EVENTS.MISSED_PUNCH_REQUEST_TIMESHEET_BOUNDARY_VIOLATION_ATTEMPTED, {
        actor: "worker",
        clockAction: "break",
        errorType: currentValidation.errorType,
        shiftId,
        workplaceId,
        selectedTime: currentBreakStartTime?.toISOString(),
        clockInTime: clockInTime.toISOString(),
        clockOutTime: clockOutTime.toISOString(),
        verifiedBreakStartTime: verifiedBreakStartTime?.toISOString(),
        verifiedBreakEndTime: verifiedBreakEndTime?.toISOString(),
      });
    }

    if (!isDefined(currentBreakStartTime)) {
      // Don't close, let user see that start time is required
      return;
    }

    if (currentValidation.errorMessage) {
      // Don't close, let user see the error
      return;
    }

    // No errors, proceed with save (convert back to UTC for parent)
    onConfirm(currentBreakStartTime, currentBreakDuration);
    modalState.closeModal();
  };

  const handleCancel = () => {
    onCancel?.();
    modalState.closeModal();
  };

  const handleTimeChange = useCallback(
    (newTime: Date | undefined) => {
      if (isDefined(newTime)) {
        // Infer the correct date based on the shift window
        // For breaks, use 0 early buffer since breaks must be after clock-in
        const inferResult = inferDateFromTime({
          timeInput: { hour: newTime.getHours(), minute: newTime.getMinutes() },
          shiftStart: getLocalTimeForPicker(shiftStartAt, workplaceTimezone),
          shiftEnd: getLocalTimeForPicker(shiftEndAt, workplaceTimezone),
          earlyBufferMinutes: 0,
        });

        // When success is false, time is outside valid window - still set it but validation will catch it
        const timeToSet = inferResult.success ? inferResult.date : newTime;
        setValue("breakStart", timeToSet);

        // If this was a skipped break and user is modifying time, automatically:
        // 1. Set break duration to 30 minutes
        // 2. Mark break as no longer skipped
        if (localBreakSkipped) {
          setValue("breakDuration", defaultBreakDuration);
          setLocalBreakSkipped(false);
        }

        // Clear save attempted flag when user makes changes
        setSaveAttempted(false);
      }
    },
    [localBreakSkipped, defaultBreakDuration, setValue, shiftStartAt, shiftEndAt, workplaceTimezone]
  );

  const handleDurationChange = (newDuration: Duration) => {
    setValue("breakDuration", newDuration);

    const isNewDurationZero = newDuration.hours === 0 && newDuration.minutes === 0;

    // If break starts as skipped but HCP selects non-zero duration
    if (localBreakSkipped && !isNewDurationZero) {
      // Automatically force the break_start_at to be the middle of the shift
      const middleTime = calculateMiddleOfShift(clockInTime, clockOutTime);
      // Convert UTC middle time to local time for the picker
      const localMiddleTime = getLocalTimeForPicker(middleTime, workplaceTimezone);
      setValue("breakStart", localMiddleTime);

      setLocalBreakSkipped(false);
    }
    // If break starts as NON-skipped, selecting the 'Skipped' option (duration = 0)
    else if (!localBreakSkipped && isNewDurationZero) {
      // Force the 'Break start' timestamp to render as N/A
      setValue("breakStart", undefined);
      setLocalBreakSkipped(true);
    }

    // Clear save attempted flag when user makes changes
    setSaveAttempted(false);
  };

  const toggleSelector = (selector: ActiveSelector) => {
    setActiveSelector(activeSelector === selector ? undefined : selector);
  };

  useEffect(() => {
    if (activeSelector === "breakStart" && localBreakSkipped) {
      handleTimeChange(getDefaultBreakStartTime());
    }
  }, [activeSelector, localBreakSkipped, handleTimeChange, getDefaultBreakStartTime]);

  // For skipped breaks, we're always valid. For non-skipped, need start time and no errors
  const isValid = localBreakSkipped || (isDefined(currentBreakStartTime) && !errorMessage);

  return (
    <BottomSheetWithIntent
      modalState={modalState}
      intent={errorMessage ? "error" : undefined}
      intentContent={errorMessage}
      intentIcon={errorMessage ? "triangle-exclamation" : undefined}
      footer={
        <DialogFooter orientation="vertical" onClose={handleCancel}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!isValid || isPickerScrolling}
            onClick={handleConfirm}
          >
            Save
          </Button>
        </DialogFooter>
      }
    >
      <DialogContent>
        <Stack spacing={6}>
          <Title semibold variant="h4" component="h2">
            Edit break
          </Title>

          <Stack spacing={4}>
            <Stack direction="row" spacing={3} alignItems="center">
              <CbhIcon size="medium" type="break" />
              <Text bold sx={{ color: (theme) => theme.palette.text.primary, flexGrow: 1 }}>
                Break start
              </Text>
              <Box textAlign="right">
                <Button
                  aria-label="Select Break start"
                  variant="outlined"
                  size="small"
                  endIconType="pencil"
                  isSelected={activeSelector === "breakStart"}
                  onClick={() => {
                    toggleSelector("breakStart");
                  }}
                >
                  {formatBreakStartDisplay(
                    localBreakSkipped,
                    currentLocalBreakStartTime,
                    workplaceTimezone
                  )}
                </Button>
              </Box>
            </Stack>

            <Stack direction="row" spacing={3} alignItems="center">
              <CbhIcon size="medium" type="clock" />
              <Text bold sx={{ color: (theme) => theme.palette.text.primary, flexGrow: 1 }}>
                Break duration
              </Text>
              <Box textAlign="right">
                <Button
                  aria-label="Select Break duration"
                  variant="outlined"
                  size="small"
                  endIconType="pencil"
                  isSelected={activeSelector === "breakDuration"}
                  onClick={() => {
                    toggleSelector("breakDuration");
                  }}
                >
                  {formatBreakDuration(currentBreakDuration, localBreakSkipped)}
                </Button>
              </Box>
            </Stack>

            <Collapse in={activeSelector === "breakStart"} timeout={300}>
              <Stack spacing={2}>
                <Divider />
                <WheelTimePicker
                  preventParentTouchStart
                  value={currentLocalBreakStartTime}
                  containerSx={{
                    // make sure the time picker takes as little horizontal space as possible
                    // this preserves more area on the left/right for the bottom sheet to capture touch events
                    // for swipe-down closing (dismiss)
                    alignSelf: "center",
                  }}
                  onChange={handleTimeChange}
                  onScrollingChange={setIsPickerScrolling}
                />
              </Stack>
            </Collapse>

            <Collapse in={activeSelector === "breakDuration"} timeout={300}>
              <Stack spacing={2}>
                <Box textAlign="center">
                  <Button
                    variant={localBreakSkipped ? "contained" : "outlined"}
                    size="small"
                    onClick={() => {
                      setLocalBreakSkipped(true);
                      setValue("breakDuration", { hours: 0, minutes: 0 });
                      setValue("breakStart", undefined);
                      setSaveAttempted(false);
                    }}
                  >
                    Skipped break
                  </Button>
                </Box>

                <DurationInput
                  preventParentTouchStart
                  value={currentBreakDuration}
                  maxHours={4}
                  minutesStep={5}
                  onChange={handleDurationChange}
                />
              </Stack>
            </Collapse>
          </Stack>
        </Stack>
      </DialogContent>
    </BottomSheetWithIntent>
  );
}
/* eslint-enable max-lines */
